import { Check, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { getCsrfToken } from "@/lib/csrf";

interface Checkpoint {
	key: string;
	label: string;
	passed: boolean;
	detail: string | null;
}

interface AuditRow {
	therapistId: string;
	therapistName: string;
	therapistGender: string;
	therapistType: string;
	therapistService: string | null;
	distanceKm: number | null;
	checkpoints: Checkpoint[];
	allPassed: boolean;
}

interface AuditSummary {
	total: number;
	passed: number;
	rejected: number;
}

interface AuditResponse {
	rows: AuditRow[];
	summary: AuditSummary;
}

interface SearchContext {
	lat: number;
	lng: number;
	city: string;
	datetime: string;
	gender: string | null;
	specialization: string | null;
	serviceId: number | null;
}

interface AuditDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	searchContext: SearchContext | null;
	auditUrl: string;
}

const CHECKPOINT_LABELS = [
	"suspended",
	"status",
	"contract",
	"schedule",
	"gender",
	"service",
	"spec",
	"avail",
] as const;

export function AuditDrawer({
	open,
	onOpenChange,
	searchContext,
	auditUrl,
}: AuditDrawerProps) {
	const [data, setData] = useState<AuditResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open || !searchContext) return;

		setLoading(true);
		setError(null);

		fetch(auditUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-CSRF-Token": getCsrfToken(),
			},
			body: JSON.stringify({
				customerLat: searchContext.lat,
				customerLng: searchContext.lng,
				appointmentDatetime: searchContext.datetime,
				city: searchContext.city,
				preferredGender: searchContext.gender,
				specialization: searchContext.specialization,
				serviceId: searchContext.serviceId,
			}),
		})
			.then((r) => r.json())
			.then(setData)
			.catch(() => setError("Failed to load audit data"))
			.finally(() => setLoading(false));
	}, [open, searchContext, auditUrl]);

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="right"
				className="w-full sm:max-w-2xl overflow-y-auto"
			>
				<SheetHeader>
					<SheetTitle>🩺 Filter Audit</SheetTitle>
					<SheetDescription>
						Semua terapis di kota ini beserta status setiap filter.
					</SheetDescription>
				</SheetHeader>

				<div className="mt-6 space-y-4">
					{loading && (
						<div className="flex items-center justify-center py-12">
							<Loader2 className="size-6 animate-spin text-muted-foreground" />
						</div>
					)}

					{error && <p className="text-sm text-destructive">{error}</p>}

					{data && (
						<>
							{/* Summary */}
							<div className="flex gap-3">
								<Badge variant="outline" className="text-sm px-3 py-1">
									Total: {data.summary.total}
								</Badge>
								<Badge className="text-sm px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
									✅ Passed: {data.summary.passed}
								</Badge>
								<Badge className="text-sm px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
									❌ Rejected: {data.summary.rejected}
								</Badge>
							</div>

							{/* Legend */}
							<div className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
								{CHECKPOINT_LABELS.map((k) => (
									<span key={k} className="bg-muted px-1.5 py-0.5 rounded">
										{k}
									</span>
								))}
							</div>

							{/* Table */}
							<div className="border rounded-lg overflow-hidden">
								<div className="overflow-x-auto">
									<table className="w-full text-xs">
										<thead>
											<tr className="bg-muted/50 border-b">
												<th className="text-left px-3 py-2 font-medium">
													Therapist
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													S
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													A
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													C
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													W
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													G
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													V
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													P
												</th>
												<th className="text-center px-1.5 py-2 font-medium w-9">
													T
												</th>
												<th className="text-right px-3 py-2 font-medium">
													Dist
												</th>
											</tr>
										</thead>
										<tbody>
											{data.rows.map((row) => (
												<tr
													key={row.therapistId}
													className={`border-b last:border-0 ${
														row.allPassed
															? "bg-green-50/30 dark:bg-green-950/20"
															: "bg-red-50/20 dark:bg-red-950/10"
													}`}
												>
													<td className="px-3 py-2">
														<div className="font-medium truncate max-w-[180px]">
															{row.therapistName}
														</div>
													</td>
													{row.checkpoints.map((cp) => (
														<td
															key={cp.key}
															className="text-center px-1.5 py-2"
															title={cp.detail || cp.label}
														>
															{cp.passed ? (
																<Check className="size-3.5 text-green-600 inline" />
															) : (
																<X className="size-3.5 text-red-500 inline" />
															)}
														</td>
													))}
													<td className="text-right px-3 py-2 text-muted-foreground">
														{row.distanceKm != null
															? `${row.distanceKm} km`
															: "—"}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>

							{/* Detail list for rejected rows */}
							<div className="space-y-2">
								{data.rows
									.filter((r) => !r.allPassed)
									.map((row) => (
										<div
											key={row.therapistId}
											className="text-xs p-2 rounded bg-muted/50"
										>
											<span className="font-medium">{row.therapistName}</span>
											{" — "}
											{row.checkpoints
												.filter((c) => !c.passed)
												.map((c) => c.detail)
												.join(" | ")}
										</div>
									))}
							</div>
						</>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
